import React, { useState, useContext, createContext } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { FiSearch, FiX } from "react-icons/fi";
import "./SidebarAcademy.css";
import useFetch from "../hooks/useFetch.js";

export const SearchContext = createContext();

const Sidebar = ({ searchContextValue }) => {
  const location = useLocation();
  const navigate = useNavigate();

  // Use provided context value or fallback to local state
  const [localSearchTerm, setLocalSearchTerm] = useState("");

  const searchTerm = searchContextValue?.searchTerm || localSearchTerm;
  const setSearchTerm = searchContextValue?.setSearchTerm || setLocalSearchTerm;

  // Fetch academy data for global search
  const {
    loading: academyLoading,
    data: academyData,
    error: academyError,
  } = useFetch(
    `https://devrw2.blob.core.windows.net/rw-website/strapi-data/academy/data.json`
  );

  const academyItems = Array.isArray(academyData) ? academyData : [];

  // Category to route mapping
  const categoryRoutes = {
    "For Corporates": "/academy/for-corporates/",
    "For Individuals": "/academy/for-individuals/",
    "Career Advancement Courses": "/academy/career-advancement-courses/",
    "professional Growth Courses": "/academy/professional-growth-courses/",
    "Bite-sized Courses": "/academy/bite-sized-courses/",
    Workshops: "/academy/workshops/",
  };

  const categories = [
    {
      name: "Artificial Intelligence Courses",
      isHeading: true,
      children: ["For Individuals", "For Corporates"],
    },
    {
      name: "For Individuals",
      path: "/academy/for-individuals/",
      parent: "AI Courses",
    },
    {
      name: "For Corporates",
      path: "/academy/for-corporates/",
      parent: "AI Courses",
    },
    {
      name: "Digital Skills Courses",
      isHeading: true,
      children: [
        "For Indiviuals",
        "For Corporate",
        "Career Advancement Courses",
        "Professional Growth Courses",
        "Bite-sized Courses",
      ],
    },
    {
      name: "For Individuals",
      isHeading: true,
      children: [
        "Career Advancement Courses",
        "Professional Growth Courses",
        "Bite-sized Courses",
      ],
    },
    {
      name: "Career Advancement Courses",
      path: "/academy/career-advancement-courses/",
      parent: "Digital Transformation Course for Individuals",
    },
    {
      name: "Professional Growth Courses",
      path: "/academy/professional-growth-courses/",
      parent: "Digital Transformation Course for Individuals",
    },
    {
      name: "Bite-sized Courses",
      path: "/academy/bite-sized-courses/",
      parent: "Digital Transformation Course for Individuals",
    },
    {
      name: "For Corporate",
      isHeading: true,
      children: ["Workshops"],
    },
    {
      name: "Workshops",
      path: "/academy/workshops",
      parent: "Digital Transformation Course for Corporates",
    },
  ];

  // Find matching courses based on search term
  const findMatchingCourse = (searchTerm) => {
    if (!searchTerm.trim() || academyLoading) return null;

    const searchLower = searchTerm.toLowerCase();
    return academyItems.find((course) =>
      course.academyTitle?.toLowerCase().includes(searchLower)
    );
  };

  // Handle search with navigation
  const handleSearch = (e) => {
    if (e.key === "Enter" && searchTerm.trim()) {
      const matchingCourse = findMatchingCourse(searchTerm);

      if (matchingCourse) {
        const targetRoute = categoryRoutes[matchingCourse.academyCategory];
        if (targetRoute) {
          // Navigate to the appropriate page with search term and fromSearch flag
          const searchParams = new URLSearchParams();
          searchParams.set("search", searchTerm);
          searchParams.set("fromSearch", "true"); // Add this to indicate navigation from search

          navigate(`${targetRoute}?${searchParams.toString()}`);
        }
      }
    }
  };

  const lowerSearchTerm = searchTerm.toLowerCase();
  const matchedHeadings = new Set();

  const filteredCategories = categories.filter((category) => {
    if (!searchTerm) return false;

    const isMatch = category.name.toLowerCase().includes(lowerSearchTerm);

    if (isMatch && category.isHeading) {
      matchedHeadings.add(category.name);
      return true;
    }
    if (isMatch && category.parent) {
      matchedHeadings.add(category.parent);
      return true;
    }
    return false;
  });

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
  };

  const clearSearch = () => {
    setSearchTerm("");
  };

  // Get search suggestions
  const getSearchSuggestions = () => {
    if (!searchTerm.trim() || academyLoading) return [];

    const searchLower = searchTerm.toLowerCase();
    return academyItems
      .filter((course) =>
        course.academyTitle?.toLowerCase().includes(searchLower)
      )
      .slice(0, 5); // Limit to 5 suggestions
  };

  const searchSuggestions = getSearchSuggestions();

  const handleSuggestionClick = (course) => {
    setSearchTerm(course.academyTitle);
    const targetRoute = categoryRoutes[course.academyCategory];
    if (targetRoute) {
      // Navigate with fromSearch flag when clicking suggestion
      const searchParams = new URLSearchParams();
      searchParams.set("search", course.academyTitle);
      searchParams.set("fromSearch", "true");

      navigate(`${targetRoute}?${searchParams.toString()}`);
    }
  };

  const renderCategories = (isDesktop) => {
    return categories.map((category, index) => {
      if (isDesktop) {
        if (category.isHeading) {
          return (
            <li key={index} className="mb-3">
              <div
                className={`px-4 py-3 font-semibold ${
                  category.name === "For Individuals" ||
                  category.name === "For Corporate"
                    ? "text-black"
                    : "text-[#666666] border-l-2 border-black"
                }`}
                style={
                  category.name === "For Individuals" ||
                  category.name === "For Corporate"
                    ? {}
                    : { background: "rgba(111, 111, 111, 0.1)" }
                }
              >
                {category.name}
              </div>
            </li>
          );
        }

        return (
          <li key={index} className="mb-3">
            <Link
              to={category.path}
              className={`block px-4 py-3 rounded-md transition-all ${
                location.pathname === category.path
                  ? "text-[#666666]  bg-[rgba(218,239,255,1)]  "
                  : "hover:bg-gray-50 text-gray-600"
              }`}
            >
              {category.name}
            </Link>
          </li>
        );
      }

      if (category.isHeading) {
        if (!matchedHeadings.has(category.name)) return null;
        return (
          <li key={index} className="mb-3">
            <div
              className={`px-4 py-3 font-semibold ${
                category.name === "For Individuals" ||
                category.name === "For Corporate"
                  ? "text-black"
                  : "text-[#666666] border-l-2 border-black"
              }`}
              style={
                category.name === "For Individuals" ||
                category.name === "For Corporate"
                  ? {}
                  : { background: "rgba(111, 111, 111, 0.1)" }
              }
            >
              {category.name}
            </div>
          </li>
        );
      }

      if (
        filteredCategories.includes(category) ||
        matchedHeadings.has(category.parent)
      ) {
        return (
          <li key={index} className="mb-3">
            <Link
              to={category.path}
              className={`block px-4 py-3 rounded-md transition-all ${
                location.pathname === category.path
                  ? "text-[rgba(62,89,183,1)] font-bold "
                  : "hover:bg-gray-50 text-gray-600"
              }`}
            >
              {category.name}
            </Link>
          </li>
        );
      }

      return null;
    });
  };

  return (
    <div className="w-full md:w-5/12 lg:w-1/4 p-4 sidebar h-full bg-white font-display">
      <div className="relative mb-6">
        <input
          type="text"
          placeholder="Search for Course"
          value={searchTerm}
          onChange={handleSearchChange}
          onKeyPress={handleSearch}
          className="w-full px-4 py-2 pr-16 rounded-md border border-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500"
        />
        <FiSearch className="absolute top-3 right-10 text-gray-400" />
        {searchTerm && (
          <button
            onClick={clearSearch}
            className="absolute top-3 right-4 text-gray-400 hover:text-gray-600 transition-colors"
            title="Clear search"
          >
            <FiX size={16} />
          </button>
        )}

        {/* Search Suggestions Dropdown */}
        {searchTerm && searchSuggestions.length > 0 && (
          <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-300 rounded-md shadow-lg z-50 max-h-60 overflow-y-auto">
            {searchSuggestions.map((course, index) => (
              <div
                key={course.id || index}
                onClick={() => handleSuggestionClick(course)}
                className="px-4 py-2 hover:bg-gray-100 cursor-pointer border-b border-gray-100 last:border-b-0"
              >
                <div className="font-medium text-sm text-gray-800">
                  {course.academyTitle}
                </div>
                <div className="text-xs text-gray-500">
                  {course.academyCategory}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Search results indicator */}
      {searchTerm && (
        <div className="mb-4 text-sm text-gray-600 px-4">
          <span>Searching for: "{searchTerm}"</span>
          <button
            onClick={clearSearch}
            className="ml-2 text-blue-600 hover:text-blue-800 underline text-xs"
          >
            Clear
          </button>
          {searchSuggestions.length > 0 && (
            <div className="text-xs text-gray-500 mt-1">
              Press Enter to navigate to matching course
            </div>
          )}
        </div>
      )}

      <ul className="hidden md:block">{renderCategories(true)}</ul>

      {searchTerm && <ul className="md:hidden">{renderCategories(false)}</ul>}
    </div>
  );
};

export default Sidebar;
